import { isApiFailureBody, isApiSuccessBody } from "@/lib/api/api-envelope"

export class DeleteMediaError extends Error {
  readonly status: number
  readonly code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = "DeleteMediaError"
    this.status = status
    this.code = code
  }
}

export type MyMemoryMedia = {
  id: string
  eventId: string
  uploadedByUserId: string
  userId: string
  /** Always true for this list (viewer-owned rows); drives lightbox delete affordance. */
  canDelete?: boolean
  eventCreatedByUserId: string
  eventOwnerId: string
  type: "image" | "video"
  url: string
  thumbnailUrl: string
  imagePixelWidth?: number
  imagePixelHeight?: number
  isHeroCandidate?: boolean
  createdAt: string
  hlsUrl?: string
  embedUrl?: string
  /** From server metadata when available; drives lightbox video stage sizing. */
  videoAspectRatio?: number
  eventTitle: string
  eventDate: string
  eventTime: string | null
  memoryRole: "hosted" | "attended"
}

async function parseEnvelope<T>(res: Response, label: string): Promise<T> {
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    const msg = isApiFailureBody(body)
      ? body.error.message
      : `${label} failed: ${res.status}`
    throw new Error(msg)
  }
  if (!isApiSuccessBody<T>(body)) {
    throw new Error(`${label} failed: malformed response`)
  }
  return body.data
}

type FetchOpts = { signal?: AbortSignal }

export async function fetchMyMedia(opts?: FetchOpts): Promise<MyMemoryMedia[]> {
  const res = await fetch("/api/media/my", {
    method: "GET",
    cache: "no-store",
    signal: opts?.signal,
  })
  return parseEnvelope<MyMemoryMedia[]>(res, "GET /api/media/my")
}

export type DeleteMediaClientStatus = "success" | "accepted_cleanup_pending"

/** `DELETE /api/media/:id` — server authorizes uploader or host; throws {@link DeleteMediaError} on failure. */
export async function deleteMedia(
  mediaId: string,
  opts?: FetchOpts,
): Promise<DeleteMediaClientStatus> {
  const id = mediaId.trim()
  if (!id) {
    throw new DeleteMediaError("Invalid media id", 400)
  }
  const res = await fetch(`/api/media/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "same-origin",
    signal: opts?.signal,
  })
  const body: unknown = await res.json().catch(() => null)
  if (!res.ok || !body || isApiFailureBody(body)) {
    const msg =
      body && isApiFailureBody(body)
        ? body.error.message
        : `DELETE /api/media/${id} failed: ${res.status}`
    const code =
      body && isApiFailureBody(body) ? body.error.code : undefined
    throw new DeleteMediaError(msg, res.status, code)
  }
  if (!isApiSuccessBody<{ success: true; status?: string }>(body)) {
    throw new DeleteMediaError(
      `DELETE /api/media/${id} failed: malformed response`,
      res.status,
    )
  }
  const st = body.data.status
  if (st === "accepted_cleanup_pending") return "accepted_cleanup_pending"
  return "success"
}
